/**
 * RBAC-4.4 · What one person can actually do, and where each permission comes from (FR-17, UX-DR14).
 *
 * The second click of "why can Dana approve a pay period": open her record, then "What can Dana do?".
 * Lists what did not come with her role first and sets it apart (AC 2), each with its origin and what
 * changing her role would do to it; then what her role gives, by area (AC 1). The set is the authority
 * function's (AC 3): `person_effective_permissions()` returns `cost_holds_permission_for()` per row.
 *
 * Collapsed until asked, so opening a record costs no extra request. Inside RBAC-4.1's PersonRoleCard,
 * so shown only to a `roles.administer` holder; the database refuses anyone else (42501).
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  effectivePermissionsView,
  type EffectiveItem,
  type EffectivePermissionsView,
} from '@/lib/personEffectivePermissions';
import { fetchPersonEffectivePermissions } from '@/services/personEffectivePermissionsService';

export interface EffectivePermissionsSectionProps {
  userId: string;
  firstName: string;
  /** Changes when the person's roles do, so an open list follows a role change. */
  version: unknown;
}

export function EffectivePermissionsSection({ userId, firstName, version }: EffectivePermissionsSectionProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<EffectivePermissionsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setView(effectivePermissionsView(await fetchPersonEffectivePermissions(userId)));
    } catch (e) {
      setView(null);
      setError(e instanceof Error && e.message.trim() ? e.message : 'Their permissions could not be loaded. Check your connection and try again.');
    }
  }, [userId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load, version]);

  const name = firstName.trim() || 'this person';
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="space-y-2 border-t pt-3" data-testid="person-effective-permissions">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-2 gap-1 px-2"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Chevron className="h-4 w-4" aria-hidden />
        What can {name} do?
      </Button>

      {open && error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {open && !error && !view && <Loader2 className="h-4 w-4 animate-spin text-primary" />}

      {open && view && (
        <div className="space-y-3 text-sm">
          <p data-testid="effective-summary">{view.summary}</p>

          {view.inactiveNotice && <p className="font-medium" data-testid="effective-inactive">{view.inactiveNotice}</p>}

          {view.overrideNotice && (
            <p
              className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="effective-override"
            >
              {view.overrideNotice}
            </p>
          )}

          {view.elsewhere.length > 0 && (
            <section
              className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-500/40 dark:bg-amber-950/40"
              data-testid="effective-elsewhere"
            >
              <h4 className="font-medium text-amber-950 dark:text-amber-100">{view.elsewhereHeading}</h4>
              <ul className="space-y-2">
                {view.elsewhere.map((item) => (
                  <ItemRow key={item.key} item={item} notFromRole />
                ))}
              </ul>
            </section>
          )}

          {view.fromRole.length > 0 && (
            <section className="space-y-2" data-testid="effective-from-role">
              {view.fromRoleHeading && <h4 className="font-medium">{view.fromRoleHeading}</h4>}
              {view.fromRole.map((group) => (
                <div key={group.area} className="space-y-1">
                  <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{group.area}</h5>
                  <ul className="space-y-1.5">
                    {group.items.map((item) => (
                      <ItemRow key={item.key} item={item} />
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )}

          {view.unlisted && <p className="text-xs text-muted-foreground">{view.unlisted}</p>}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, notFromRole = false }: { item: EffectiveItem; notFromRole?: boolean }) {
  // Under "From their role" the heading already names that origin: list only the others. A row held by
  // override riding on their role has no plain "Their role," origin, so its one origin is shown as is.
  const viaRole = item.origins.some((o) => o.startsWith('Their role,'));
  const origins = notFromRole || !viaRole ? item.origins : item.origins.filter((o) => !o.startsWith('Their role,'));
  const prefix = notFromRole ? 'From ' : viaRole ? 'Also from ' : 'By ';
  return (
    <li className="space-y-0.5" data-testid="effective-item">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-medium">{item.title}</span>
        {notFromRole && (
          <Badge variant="outline" className="border-amber-400 text-amber-900 dark:border-amber-500/60 dark:text-amber-100">
            Not from their role
          </Badge>
        )}
      </div>
      {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
      {origins.length > 0 && (
        <p className="text-xs" data-testid="effective-origin">
          {prefix}
          {origins.join('; ')}
        </p>
      )}
      {item.roleChange && <p className="text-xs font-medium">{item.roleChange}</p>}
    </li>
  );
}
